/**
 * Iframe Authentication Service
 *
 * Handles postMessage communication with PLG-frontend (parent):
 * - AUTH_TOKEN: exchanges the Cognito JWT for a B2B session token
 * - NAVIGATE: pushes the requested route inside the iframe
 * - REQUEST_BRANDS: returns the retailer's direct-brand list
 *
 * When VUE_APP_PLG_URL is set, only messages from that origin are accepted.
 * Replies are always sent to event.origin (never '*').
 */

import store from '@/store'
import router from '@/router'
import VueRouter from 'vue-router'
import { Settings } from '@/Settings'
import { GET_COMMON_AUTH } from '@/store/modules/common.module'
import { IFRAME_TOKEN_EXCHANGE } from '@/store/modules/auth.module'
import { isInIframe } from '@/utils/iframe-detect'

// Don't import INIT_SHOP from '@/store/modules/shop.module' here:
// shop.module.js already imports sendCartCountToParent/isInIframe from this
// file, and a back-import creates a runtime cycle. Webpack returns a partial
// module during init, so INIT_SHOP can resolve to `undefined`; committing
// that throws *after* AUTH_SUCCESSFUL has already been sent, and PLG then
// receives a stray AUTH_ERROR that flips isSuccess back to false. Reference
// the mutation type by its string id below — keep in sync with shop.module.
const INIT_SHOP_MUTATION = 'initShop'

const { isNavigationFailure, NavigationFailureType } = VueRouter

const MESSAGE_TYPES = {
  AUTH_TOKEN: 'AUTH_TOKEN',
  AUTH_SUCCESSFUL: 'AUTH_SUCCESSFUL',
  AUTH_ERROR: 'AUTH_ERROR',
  NAVIGATE: 'NAVIGATE',
  ROUTE_CHANGED: 'ROUTE_CHANGED',
  REQUEST_BRANDS: 'REQUEST_BRANDS',
  BRANDS_DATA: 'BRANDS_DATA',
  CART_COUNT_UPDATED: 'CART_COUNT_UPDATED',
  DOWNLOAD_INDICATOR: 'DOWNLOAD_INDICATOR',
  MODAL_OPEN: 'MODAL_OPEN',
  MODAL_CLOSE: 'MODAL_CLOSE',
  WISHLIST_INDICATOR: 'WISHLIST_INDICATOR',
  LINE_SHEET_OUTREACH: 'LINE_SHEET_OUTREACH',
}

// Module-level state
let _verifiedOrigin = null
let _iframeReadyCallback = null
let _iframeReadyFired = false
let _unregisterRouteReporter = null
let _openModalCount = 0

/**
 * Validates if the message is an AUTH_TOKEN message
 */
function isAuthTokenMessage(data) {
  return (
    data
    && typeof data === 'object'
    && data.type === MESSAGE_TYPES.AUTH_TOKEN
    && typeof data.token === 'string'
    && data.token.length > 0
  )
}

/**
 * Validates if the message is a NAVIGATE message (path must be non-empty string).
 */
function isNavigateMessage(data) {
  return (
    data
    && typeof data === 'object'
    && data.type === MESSAGE_TYPES.NAVIGATE
    && typeof data.path === 'string'
    && data.path.length > 0
  )
}

/**
 * Validates if the message is a REQUEST_BRANDS message
 */
function isRequestBrandsMessage(data) {
  return (
    data
    && typeof data === 'object'
    && data.type === MESSAGE_TYPES.REQUEST_BRANDS
  )
}

/** Pairs of (validator, message type) for dispatch. */
const MESSAGE_TYPE_DETECTORS = [
  [isAuthTokenMessage, MESSAGE_TYPES.AUTH_TOKEN],
  [isNavigateMessage, MESSAGE_TYPES.NAVIGATE],
  [isRequestBrandsMessage, MESSAGE_TYPES.REQUEST_BRANDS],
]

/**
 * Returns the message type key for dispatch, or null if unknown/invalid.
 */
function getMessageType(data) {
  if (!data || typeof data !== 'object') return null
  for (const [check, type] of MESSAGE_TYPE_DETECTORS) {
    if (check(data)) return type
  }
  return null
}

/**
 * Returns true if the event origin is allowed (PLG origin check when configured).
 */
function isOriginAllowed(event) {
  const allowedOrigin = getAllowedParentOrigin()
  if (!allowedOrigin) {
    console.warn('[Iframe Auth] VUE_APP_PLG_URL is not configured — accepting message from:', event.origin)
    return true
  }
  return event.origin === allowedOrigin
}

/**
 * Returns configured PLG origin if set; used to validate incoming messages.
 */
function getAllowedParentOrigin() {
  try {
    const origin = Settings.getConfig('VUE_APP_PLG_URL')
    return typeof origin === 'string' && origin.length > 0 ? origin : null
  } catch {
    return null
  }
}

/**
 * Sends a response message to the parent window.
 * Requires an explicit targetOrigin — never falls back to '*'.
 */
function sendMessageToParent(message, targetOrigin) {
  if (!targetOrigin) {
    console.warn('[Iframe Auth] No targetOrigin provided, refusing to send message')
    return
  }
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(message, targetOrigin)
  }
}

/**
 * Sends an AUTH_SUCCESSFUL response to the parent window.
 */
function sendSuccessResponse(targetOrigin) {
  sendMessageToParent({
    type: MESSAGE_TYPES.AUTH_SUCCESSFUL,
    timestamp: Date.now(),
  }, targetOrigin)
}

/** Returns backend error message from Axios response if present, otherwise null. */
function getServerErrorMessage(error) {
  const msg = error?.response?.data?.message
  return typeof msg === 'string' && msg.length > 0 ? msg : null
}

/**
 * Extracts a human-readable message from an Axios or generic Error.
 * Prefers backend error message (error.response.data.message) over error.message.
 */
function getErrorMessage(error, fallback = 'Unknown error') {
  if (!error) return fallback
  return getServerErrorMessage(error) || error?.message || fallback
}

/**
 * Sends an AUTH_ERROR response to the parent window.
 * @param {string} targetOrigin - Origin to send to
 * @param {string} errorCode - Structured code for the parent to distinguish error types (e.g. 'TOKEN_EXCHANGE_FAILED')
 * @param {string} errorMessage - Human-readable message (prefer backend message when available)
 */
function sendErrorResponse(targetOrigin, errorCode, errorMessage) {
  sendMessageToParent({
    type: MESSAGE_TYPES.AUTH_ERROR,
    error: errorCode,
    errorMessage: errorMessage || 'Authentication failed',
    timestamp: Date.now(),
  }, targetOrigin)
}

/**
 * Notifies the iframeReady callback if registered
 */
function notifyIframeReady() {
  if (_iframeReadyCallback && !_iframeReadyFired) {
    _iframeReadyFired = true
    _iframeReadyCallback()
  }
}

/**
 * Handles NAVIGATE message: push route and ignore duplicated/cancelled navigation failures.
 * Cancelled navigations are expected when rapid section switches cause a new push to supersede
 * a pending one whose async beforeEach guard hasn't resolved yet.
 */
function handleNavigateMessage(data) {
  console.info('[Iframe Auth] Received NAVIGATE message:', data.path)
  notifyIframeReady()
  router.push({ path: data.path }).catch(err => {
    if (
      isNavigationFailure(err, NavigationFailureType.duplicated)
      || isNavigationFailure(err, NavigationFailureType.cancelled)
    ) {
      return
    }
    console.error('[Iframe Auth] Navigation error:', err)
  })
}

/**
 * Performs token exchange and sends success/error response to parent.
 */
async function performTokenExchangeAndRespond(data, targetOrigin) {
  const { token, plgBrandId } = data

  await store.dispatch(IFRAME_TOKEN_EXCHANGE, { token, plgBrandId })

  // Set verified origin AFTER successful auth only
  _verifiedOrigin = targetOrigin

  console.info('[Iframe Auth] Token exchange successful, sending AUTH_SUCCESSFUL to parent')
  sendSuccessResponse(targetOrigin)

  runPostAuthSideEffects()

  notifyIframeReady()
  initRouteChangeReporter()

  if (!isInIframe()) {
    console.info('[Iframe Auth] Redirecting to dashboard')
    router.push({ path: '/dashboard' })
  }
}

/**
 * Best-effort post-auth side-effects: anything that throws here would be caught
 * by handleAuthTokenMessage and emit a stray AUTH_ERROR *after* AUTH_SUCCESSFUL,
 * leaving PLG in a degraded state (auth flips back to failed and chrome stops
 * populating). Each is isolated so a single failure can't poison the handshake.
 */
function runPostAuthSideEffects() {
  try {
    // INIT_SHOP runs once in main.js#beforeCreate, but on a fresh iframe load
    // (no restored session) it executes before the role is in localStorage,
    // so its `role === RETAILER` guard fails and the cart-count Vuex
    // subscriber never registers. Re-commit now that the just-completed
    // exchange has populated the role; the mutation is idempotent (guarded
    // by `_cartCountSubscribed`).
    store.commit(INIT_SHOP_MUTATION)
  } catch (err) {
    console.warn('[Iframe Auth] post-auth INIT_SHOP commit failed:', err)
  }

  try {
    // Send current cart count now that origin is verified (initial fetch
    // during INIT_SHOP fires before auth completes, so that first message
    // is dropped).
    const cartCount = store.state.shop?.cartItemsCount
    if (cartCount != null) {
      sendCartCountToParent(cartCount)
    }
  } catch (err) {
    console.warn('[Iframe Auth] post-auth cart count send failed:', err)
  }

  try {
    // If a download was triggered before _verifiedOrigin was set, or the
    // postMessage arrived at PLG before its useB2BMessageListener attached,
    // re-emit so the red dot survives the auth handshake gap.
    if (store.state.notification?.downloadsNavIndicator) {
      sendDownloadIndicatorToParent()
    }
  } catch (err) {
    console.warn('[Iframe Auth] post-auth download indicator re-emit failed:', err)
  }
}

/**
 * Handles AUTH_TOKEN message: exchange token and respond to parent
 */
async function handleAuthTokenMessage(data, targetOrigin) {
  console.info('[Iframe Auth] Received AUTH_TOKEN message from parent')
  try {
    await performTokenExchangeAndRespond(data, targetOrigin)
  } catch (error) {
    console.error('[Iframe Auth] Token exchange failed:', error)
    sendErrorResponse(
      targetOrigin,
      'TOKEN_EXCHANGE_FAILED',
      getErrorMessage(error, 'Token exchange failed'),
    )
  }
}

/**
 * Sends BRANDS_DATA message to the parent.
 */
function sendBrandsDataToParent(targetOrigin, brands) {
  sendMessageToParent({
    type: MESSAGE_TYPES.BRANDS_DATA,
    brands,
    timestamp: Date.now(),
  }, targetOrigin)
}

/**
 * Sends the current cart count to the PLG parent window.
 * Called by the Vuex subscriber whenever cartItemsCount changes.
 */
export function sendCartCountToParent(count) {
  sendMessageToParent({
    type: MESSAGE_TYPES.CART_COUNT_UPDATED,
    count,
    timestamp: Date.now(),
  }, _verifiedOrigin)
}

/**
 * Resolves a parent origin for fire-and-forget lifecycle messages (modal
 * open/close). Modal lifecycle is non-sensitive, so we don't gate on the
 * post-auth `_verifiedOrigin`: prefer it, then the configured PLG URL, then
 * fall back to '*'. The parent's listener still filters by origin on its
 * side, so a permissive sender doesn't widen attack surface.
 */
function getLifecycleParentTarget() {
  if (_verifiedOrigin) return _verifiedOrigin
  const allowed = getAllowedParentOrigin()
  if (allowed) return allowed
  return '*'
}

/**
 * Notifies the PLG parent window that a modal opened inside the iframe.
 * Ref-counts so nested/sibling modals only emit one MODAL_OPEN (0→1) and
 * one MODAL_CLOSE (1→0).
 */
export function notifyModalOpened() {
  _openModalCount += 1
  if (_openModalCount !== 1) return
  if (!window.parent || window.parent === window) return
  const target = getLifecycleParentTarget()
  console.info('[Iframe Auth] sending MODAL_OPEN to parent', { target })
  window.parent.postMessage(
    { type: MESSAGE_TYPES.MODAL_OPEN, timestamp: Date.now() },
    target,
  )
}

export function notifyModalClosed() {
  if (_openModalCount === 0) return
  _openModalCount -= 1
  if (_openModalCount !== 0) return
  if (!window.parent || window.parent === window) return
  const target = getLifecycleParentTarget()
  console.info('[Iframe Auth] sending MODAL_CLOSE to parent', { target })
  window.parent.postMessage(
    { type: MESSAGE_TYPES.MODAL_CLOSE, timestamp: Date.now() },
    target,
  )
}

/**
 * Notifies the PLG parent window that a new download is available.
 * Called by the notification module when a download indicator is triggered.
 */
export function sendDownloadIndicatorToParent() {
  sendMessageToParent({
    type: MESSAGE_TYPES.DOWNLOAD_INDICATOR,
    timestamp: Date.now(),
  }, _verifiedOrigin)
}

/**
 * Notifies the PLG parent window that a wishlist was created/updated.
 * Called by the notification module when a wishlist indicator is triggered.
 */
export function sendWishlistIndicatorToParent() {
  sendMessageToParent({
    type: MESSAGE_TYPES.WISHLIST_INDICATOR,
    timestamp: Date.now(),
  }, _verifiedOrigin)
}

/**
 * Hands a wishlist line sheet off to the PLG parent for outreach (KP-3326,
 * Path 4). The parent routes to its sequence/template drafting flow with the
 * public link pre-attached. `destination` is 'sequence' | 'template'; `mode`
 * is 'new' | 'existing'. Sent to the verified post-auth origin, falling back
 * to the configured PLG URL — never '*', since it drives parent navigation.
 */
export function sendLineSheetOutreachToParent({ lineSheetUrl, lineSheetName, destination, mode }) {
  const target = _verifiedOrigin || getAllowedParentOrigin()
  if (!target) {
    console.warn('[Iframe Auth] No PLG origin configured — cannot send LINE_SHEET_OUTREACH')
    return false
  }
  sendMessageToParent({
    type: MESSAGE_TYPES.LINE_SHEET_OUTREACH,
    lineSheetUrl,
    lineSheetName,
    destination,
    mode,
    timestamp: Date.now(),
  }, target)
  return true
}

/**
 * Handles REQUEST_BRANDS message: fetch direct brands via common/auth and send to parent.
 * Only responds after successful token exchange; request origin must match _verifiedOrigin.
 * Mirrors the B2B Explore nav pattern (buildDirectBrandsMenu in VerticalNavMenu.vue).
 */
async function handleRequestBrandsMessage(targetOrigin) {
  if (!_verifiedOrigin || targetOrigin !== _verifiedOrigin) {
    console.warn('[Iframe Auth] REQUEST_BRANDS ignored: token exchange not completed or origin mismatch')
    return
  }
  console.info('[Iframe Auth] Received REQUEST_BRANDS message, fetching direct brands...')
  try {
    const response = await store.dispatch(GET_COMMON_AUTH)
    const directBrands = response?.data?.data?.DIRECT_BRANDS || []
    const brands = directBrands.map(b => ({ _id: b._id, name: b.name, logo: b.logo }))
    sendBrandsDataToParent(targetOrigin, brands)
    console.info(`[Iframe Auth] Sent BRANDS_DATA with ${brands.length} direct brands`)
  } catch (error) {
    console.error('[Iframe Auth] Failed to fetch brands:', error)
    sendBrandsDataToParent(targetOrigin, [])
  }
}

/**
 * Normalizes event.data to an object (parses JSON string if needed)
 */
function getMessageData(event) {
  const raw = event.data
  if (raw && typeof raw === 'object') {
    return raw
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }
  return null
}

/** Message type to handler; each receives (data, origin). */
const MESSAGE_HANDLERS = {
  [MESSAGE_TYPES.AUTH_TOKEN]: (data, origin) => handleAuthTokenMessage(data, origin),
  [MESSAGE_TYPES.NAVIGATE]: data => handleNavigateMessage(data),
  [MESSAGE_TYPES.REQUEST_BRANDS]: (_data, origin) => handleRequestBrandsMessage(origin),
}

/**
 * Handles incoming postMessage events.
 * When VUE_APP_PLG_URL is configured, only messages from that origin are accepted.
 * Replies are always sent to event.origin (never '*').
 */
function handleMessage(event) {
  if (!isOriginAllowed(event)) return

  const data = getMessageData(event)
  if (!data || typeof data !== 'object') return
  if (!isInIframe()) return

  const type = getMessageType(data)
  const handler = type && MESSAGE_HANDLERS[type]
  if (handler) handler(data, event.origin)
}

// isInIframe is imported from @/utils/iframe-detect (zero-dependency module)
// and re-exported here so existing consumers don't need to change their imports.

/**
 * Initializes the route change reporter.
 * Reports route changes to the verified parent origin via ROUTE_CHANGED messages.
 */
function initRouteChangeReporter() {
  if (_unregisterRouteReporter) return // already registered
  if (!_verifiedOrigin) return

  _unregisterRouteReporter = router.afterEach(to => {
    if (!_verifiedOrigin) return
    sendMessageToParent({
      type: MESSAGE_TYPES.ROUTE_CHANGED,
      path: to.fullPath,
    }, _verifiedOrigin)
  })
}

/**
 * Registers a callback to be called when iframe auth is ready.
 * Handles race condition: if auth already succeeded, calls callback immediately.
 */
export function onIframeReady(callback) {
  _iframeReadyCallback = callback
  if (_iframeReadyFired) {
    callback()
  }
}

/**
 * Returns the verified origin (set after successful auth)
 */
export function getVerifiedOrigin() {
  return _verifiedOrigin
}

/**
 * Initializes the iframe authentication listener.
 * Always registers the listener so messages are received when the app is embedded;
 * when not in an iframe, no AUTH_TOKEN messages will be sent by a parent.
 */
export function initIframeAuthListener() {
  window.addEventListener('message', handleMessage)
}

/**
 * Removes the iframe authentication listener and cleans up state
 */
export function removeIframeAuthListener() {
  window.removeEventListener('message', handleMessage)
  if (_unregisterRouteReporter) {
    _unregisterRouteReporter()
    _unregisterRouteReporter = null
  }
  _verifiedOrigin = null
  _iframeReadyFired = false
  _iframeReadyCallback = null
}

export { isInIframe, sendMessageToParent }

export default {
  initIframeAuthListener,
  removeIframeAuthListener,
  isInIframe,
  onIframeReady,
  getVerifiedOrigin,
  sendMessageToParent,
  sendCartCountToParent,
}
